import * as z from 'zod';

import config from '@config';
import { READER_ID_KEY } from '@/constants';

import type { Envelope } from './envelope';
import { isSharingNow } from './log/event-log';
import { uuid } from './uuid';

/**
 * Who the reader is to analytics, per tenant (`pwa.json`'s
 * `analytics.identity`):
 *
 * - `anonymous` (the default): the session id only. Reports have sessions
 *   and no returning readers. Nothing that recognises a reader is created
 *   or sent unless the tenant asks for it.
 * - `device`: a random `reader_id` kept on the device, so a returning
 *   reader is recognised. It is not derived from anything about the person
 *   or the device, and it is per app: the same person on two apps, or two
 *   browsers, is two readers. A tenant opts in with
 *   `"analytics": { "identity": "device" }`, once it has decided it may
 *   keep such an id under its own consent rules.
 * - `account`: a hash of the account id supplied by `system`. Specified but
 *   not available: a hash computed in the browser can be reversed by anyone
 *   with the app bundle, so until `system` supplies one a tenant asking for
 *   `account` gets `device`.
 *
 * `identity_level` on the event says what the row actually carries, which
 * is not always what the tenant configured:
 *
 * - a reader who is not sharing analytics gets no `reader_id`, and none is
 *   created for them: the id exists to recognise a reader in the tenant's
 *   analytics, and their events are not going there. If they shared before,
 *   the stored id is left alone and used again if they share again.
 * - without storage to keep a `reader_id` in, an id would be new on every
 *   page, so the row is `anonymous`.
 */
export type IdentityLevel = Envelope['identity_level'];

export function configuredIdentityLevel(): IdentityLevel {
	return config.analytics?.identity ?? 'anonymous';
}

let cached: string | undefined;

function readerId(): string | undefined {
	if (cached) {
		return cached;
	}
	try {
		const stored = z.uuid().safeParse(localStorage.getItem(READER_ID_KEY));
		if (stored.success) {
			cached = stored.data;
			return cached;
		}
		const created = uuid();
		localStorage.setItem(READER_ID_KEY, created);
		// Only an id that was really kept is a reader id.
		if (localStorage.getItem(READER_ID_KEY) === created) {
			cached = created;
		}
		return cached;
	} catch {
		return undefined;
	}
}

/** The identity fields of an event recorded now. */
export function identityOf(
	level: IdentityLevel = configuredIdentityLevel(),
): Pick<Envelope, 'identity_level' | 'reader_id'> {
	if (level === 'anonymous' || !isSharingNow()) {
		return { identity_level: 'anonymous' };
	}
	const id = readerId();
	return id ? { identity_level: 'device', reader_id: id } : { identity_level: 'anonymous' };
}

/** Test-only: forget the id held in memory. */
export function resetIdentityForTests(): void {
	cached = undefined;
}
